import i18next, { type i18n as I18nInstance } from 'i18next'
import { initReactI18next } from 'react-i18next'
import enCommon from './locales/en/common.json'
import hiCommon from './locales/hi/common.json'
import taCommon from './locales/ta/common.json'

/**
 * F0-45 — i18next setup. Full localisation is MOD-11 (Phase 3); what lands here
 * is the machinery, the English source bundle, and the Hindi/Tamil skeletons so
 * the completeness gate has something to check against.
 *
 * PLURALS: i18next v24 implements Intl.PluralRules natively via the `_one` /
 * `_other` suffix convention, which covers the ICU plural categories the plan
 * calls for. A separate ICU message-format post-processor would only be needed
 * for select/selectordinal, which no current string uses — adding it now would
 * be a dependency with no consumer.
 */

export const SUPPORTED_LOCALES = [
  { code: 'en', label: 'English', dir: 'ltr' as const, script: 'latin' as const },
  { code: 'hi', label: 'हिन्दी', dir: 'ltr' as const, script: 'devanagari' as const },
  { code: 'ta', label: 'தமிழ்', dir: 'ltr' as const, script: 'tamil' as const },
] as const

export type LocaleCode = (typeof SUPPORTED_LOCALES)[number]['code']

export const DEFAULT_LOCALE: LocaleCode = 'en'

export const resources = {
  en: { common: enCommon },
  hi: { common: hiCommon },
  ta: { common: taCommon },
} as const

export interface InitI18nOptions {
  locale?: LocaleCode
  /** Marketing/console default to false; the respondent runtime sets true. */
  debug?: boolean
}

export function initI18n({ locale = DEFAULT_LOCALE, debug = false }: InitI18nOptions = {}): I18nInstance {
  if (!i18next.isInitialized) {
    void i18next.use(initReactI18next).init({
      resources,
      lng: locale,
      fallbackLng: DEFAULT_LOCALE,
      defaultNS: 'common',
      debug,
      interpolation: { escapeValue: false },
      returnNull: false,
    })
  }
  return i18next
}

/** Sets the language and keeps <html lang> in step, which screen readers rely on. */
export async function setLocale(locale: LocaleCode) {
  await i18next.changeLanguage(locale)
  if (typeof document !== 'undefined') {
    const config = SUPPORTED_LOCALES.find((l) => l.code === locale)
    document.documentElement.lang = locale
    document.documentElement.dir = config?.dir ?? 'ltr'
  }
}

export function isSupportedLocale(value: string): value is LocaleCode {
  return SUPPORTED_LOCALES.some((l) => l.code === value)
}

/** Picks the best supported locale from `navigator.languages`. */
export function detectLocale(candidates: readonly string[] = []): LocaleCode {
  for (const candidate of candidates) {
    const base = candidate.split('-')[0]?.toLowerCase()
    if (base && isSupportedLocale(base)) return base
  }
  return DEFAULT_LOCALE
}

/**
 * ML-FR-002 — number/date/currency formatting through Intl.
 *
 * Currency defaults to INR because the launch market is India and cost estimates
 * (BB-FR-004) are quoted in rupees; callers pass an explicit currency otherwise.
 */
export function createFormatters(locale: LocaleCode, currency = 'INR') {
  const localeTag = locale === 'en' ? 'en-IN' : `${locale}-IN`
  return {
    number: new Intl.NumberFormat(localeTag),
    /** Ranges only, per BR-BB-002 — never a single point estimate. */
    currency: new Intl.NumberFormat(localeTag, {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }),
    percent: new Intl.NumberFormat(localeTag, { style: 'percent', maximumFractionDigits: 1 }),
    date: new Intl.DateTimeFormat(localeTag, { dateStyle: 'medium' }),
    dateTime: new Intl.DateTimeFormat(localeTag, { dateStyle: 'medium', timeStyle: 'short' }),
    relative: new Intl.RelativeTimeFormat(localeTag, { numeric: 'auto' }),
  }
}

export { useTranslation, Trans, I18nextProvider } from 'react-i18next'
export { default as i18n } from 'i18next'
